/**
 * AddressSearch Component - WESTEER Design System
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/map-tools/address-search
 *
 * @description
 * Herramienta de búsqueda de direcciones y lugares mediante geocoding.
 * Permite encontrar ubicaciones por nombre, dirección o coordenadas.
 *
 * @remarks
 * **Características**:
 * - Búsqueda mediante geocoding API (Nominatim OpenStreetMap)
 * - Lista de resultados con detalles
 * - Navegación automática a ubicación seleccionada
 * - Marcador temporal en ubicación encontrada
 * - Panel flotante compacto
 * - Integración con MapLibre GL
 *
 * @example
 * ```tsx
 * import { AddressSearch } from '@/core/map-tools/address-search';
 *
 * function MapTools() {
 *   const [showSearch, setShowSearch] = useState(false);
 *
 *   return (
 *     <AddressSearch
 *       map={mapInstance}
 *       isOpen={showSearch}
 *       onClose={() => setShowSearch(false)}
 *     />
 *   );
 * }
 * ```
 */

import { useState, useRef, useEffect } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { Search, X, MapPin, Loader } from 'lucide-react';
import './address-search.css';

/**
 * Resultado de geocoding
 */
interface GeocodingResult {
  id: string;
  displayName: string;
  lat: number;
  lon: number;
  type: string;
  importance: number;
}

/**
 * Props para AddressSearch
 */
interface AddressSearchProps {
  /**
   * Instancia del mapa MapLibre
   */
  map: MapLibreMap | null;

  /**
   * Si el panel está abierto
   */
  isOpen: boolean;

  /**
   * Callback para cerrar el panel
   */
  onClose: () => void;
}

/**
 * AddressSearch - Herramienta de búsqueda de direcciones
 */
export function AddressSearch({ map, isOpen, onClose }: AddressSearchProps): JSX.Element | null {
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [selectedResult, setSelectedResult] = useState<GeocodingResult | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Limpia marcadores y estado cuando el panel se cierra externamente
   * o cuando el componente se desmonta.
   */
  useEffect(() => {
    if (isOpen) return;

    // Cancelar búsqueda pendiente
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Limpiar estado local
    setQuery('');
    setResults([]);
    setSelectedResult(null);

    // Limpiar marcador del mapa (pulse primero, luego marker, luego source)
    if (map) {
      if (map.getLayer('search-result-marker-pulse')) {
        map.removeLayer('search-result-marker-pulse');
      }
      if (map.getLayer('search-result-marker')) {
        map.removeLayer('search-result-marker');
      }
      if (map.getSource('search-result-marker')) {
        map.removeSource('search-result-marker');
      }
    }
  }, [isOpen, map]);

  /**
   * Buscar dirección usando Nominatim (OpenStreetMap)
   */
  const searchAddress = async (searchQuery: string): Promise<void> => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setResults([]);
      return;
    }

    setIsSearching(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
          `format=json&` +
          `q=${encodeURIComponent(searchQuery)}&` +
          `limit=5&` +
          `addressdetails=1`
      );

      if (!response.ok) {
        throw new Error('Error en la búsqueda');
      }

      const data = await response.json();

      const parsedResults: GeocodingResult[] = data.map((item: any) => ({
        id: item.place_id,
        displayName: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
        type: item.type,
        importance: item.importance,
      }));

      setResults(parsedResults);
    } catch (error) {
      console.error('[AddressSearch] Error:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Maneja cambios en el input con debounce
   */
  const handleInputChange = (value: string): void => {
    setQuery(value);

    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Buscar después de 500ms sin escribir
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value);
    }, 500);
  };

  /**
   * Selecciona un resultado y navega a él
   */
  const handleSelectResult = (result: GeocodingResult): void => {
    if (!map) return;

    setSelectedResult(result);

    // Volar a la ubicación
    map.flyTo({
      center: [result.lon, result.lat],
      zoom: 16,
      duration: 2000,
    });

    // Agregar marcador temporal
    addTemporaryMarker(result.lon, result.lat, result.displayName);

    // Limpiar búsqueda
    setQuery('');
    setResults([]);
  };

  /**
   * Agrega un marcador temporal en la ubicación encontrada
   */
  const addTemporaryMarker = (lon: number, lat: number, label: string): void => {
    if (!map) return;

    // Remover capas anteriores (pulse primero, luego marker, luego source)
    if (map.getLayer('search-result-marker-pulse')) {
      map.removeLayer('search-result-marker-pulse');
    }
    if (map.getLayer('search-result-marker')) {
      map.removeLayer('search-result-marker');
    }
    if (map.getSource('search-result-marker')) {
      map.removeSource('search-result-marker');
    }

    // Agregar nuevo marcador
    map.addSource('search-result-marker', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: { label },
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      },
    });

    map.addLayer({
      id: 'search-result-marker',
      type: 'circle',
      source: 'search-result-marker',
      paint: {
        'circle-radius': 12,
        'circle-color': '#2FA4B5',
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
      },
    });

    // Pulso animado
    map.addLayer({
      id: 'search-result-marker-pulse',
      type: 'circle',
      source: 'search-result-marker',
      paint: {
        'circle-radius': 20,
        'circle-color': '#2FA4B5',
        'circle-opacity': 0.3,
      },
    });
  };

  /**
   * Limpiar búsqueda
   */
  const handleClear = (): void => {
    setQuery('');
    setResults([]);
    setSelectedResult(null);

    // Remover marcador
    if (map) {
      if (map.getLayer('search-result-marker')) {
        map.removeLayer('search-result-marker');
      }
      if (map.getLayer('search-result-marker-pulse')) {
        map.removeLayer('search-result-marker-pulse');
      }
      if (map.getSource('search-result-marker')) {
        map.removeSource('search-result-marker');
      }
    }
  };

  /**
   * Cerrar panel
   */
  const handleClose = (): void => {
    handleClear();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="address-search-panel">
      {/* Header */}
      <div className="address-search-header">
        <div className="address-search-title-wrapper">
          <Search size={16} />
          <h3 className="address-search-title">Buscar Dirección</h3>
        </div>
        <button className="address-search-close" onClick={handleClose} aria-label="Cerrar búsqueda">
          <X size={16} />
        </button>
      </div>

      {/* Input de búsqueda */}
      <div className="address-search-input-wrapper">
        <Search size={16} className="address-search-input-icon" />
        <input
          type="text"
          className="address-search-input"
          placeholder="Buscar dirección, lugar o coordenadas..."
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          autoFocus
        />
        {query && (
          <button className="address-search-clear" onClick={handleClear} aria-label="Limpiar">
            <X size={14} />
          </button>
        )}
        {isSearching && <Loader size={16} className="address-search-loading" />}
      </div>

      {/* Resultados */}
      {results.length > 0 && (
        <div className="address-search-results">
          {results.map((result) => (
            <button
              key={result.id}
              className="address-search-result-item"
              onClick={() => handleSelectResult(result)}
            >
              <MapPin size={16} className="address-search-result-icon" />
              <div className="address-search-result-info">
                <div className="address-search-result-name">{result.displayName}</div>
                <div className="address-search-result-coords">
                  {result.lat.toFixed(6)}, {result.lon.toFixed(6)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Estado vacío */}
      {!isSearching && query.length >= 3 && results.length === 0 && (
        <div className="address-search-empty">
          <p>No se encontraron resultados para "{query}"</p>
        </div>
      )}

      {/* Resultado seleccionado */}
      {selectedResult && (
        <div className="address-search-selected">
          <MapPin size={14} />
          <span className="address-search-selected-text">Ubicación encontrada</span>
        </div>
      )}

      {/* Footer con info */}
      <div className="address-search-footer">
        <p className="address-search-info-text">
          💡 Escribe al menos 3 caracteres para buscar
        </p>
      </div>
    </div>
  );
}
